import { takeLatest, put, call, select, all, delay } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import get from 'lodash/get';
import {
    CURBSIDE_CART_CONTENT,
    ANALYTICS_ACTIONS_UPDATE_CART,
    ANALYTICS_ACTIONS_SAME_DAY_PICKUP,
} from 'yoda-checkout-components/lib/common/Constants';
import { ANALYTICS_CLICK_EVENT } from 'yoda-checkout-components/lib/actionTypes/CheckoutAnalyticsActionTypes';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import orderActions from 'yoda-site-components/lib/actions/OrderAction';
import itemActions from 'yoda-site-components/lib/actions/ItemCountAction';
import { LOG_CLIENT_ERROR } from 'yoda-site-components/lib/actionTypes/ClientLoggerActionTypes';
import { updateCartCount, addToCart } from 'yoda-site-components/lib/actions/NativeAppAction';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import { pageLoadSuccessAnalyticsAction } from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import { COCouponsSaga } from 'yoda-checkout-components/lib/sagas/COCouponsSaga';
import { setApplePayOrderError } from 'yoda-checkout-components/lib/actions/PayWithAppleAction';
import { getMessageContent } from 'yoda-checkout-components/lib/actions/MessageContentAction';

import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import {
    getZipCodeFromUPS,
    getUserZipCode,
    doPostSSRLogs,
    isAgentLogged,
    isFeatureEnabledFromCookie,
} from 'yoda-core-components/lib/helpers/Utils/Utils';
import {
    getCartItemById,
    isBOPISItem,
    getClientId,
    updateUserZipcodeBasedSTHItem,
    isOrderEligibleApplePay,
    isOrderEligibleGooglePay,
    isPayPalStrategiesAvailable,
    getCurbsideEligible,
} from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import { isAurora, updateCartUID } from 'yoda-checkout-components/lib/helpers/Utils/ATCCommonUtils';
import * as COAdjustmentActionTypes from 'yoda-checkout-components/lib/actionTypes/COAdjustmentActionTypes';
import { getOrderDetails } from 'yoda-interfaces/lib/Order/OrderApi';
import {
    SET_SERVICE_ERROR,
    LOCATION_SERVICE_COOKIES_AKAMAI_STORE,
    LOCATION_SERVICE_COOKIES_USER_PREFERRED_STORE,
} from 'yoda-site-components/lib/common/Constants';
import { selectContext } from 'yoda-site-components/lib/selectors/ContextSelector';
import { singleClickCheckoutLoadApiFailed } from 'yoda-checkout-components/lib/actions/SingleClickCheckoutActions';
import { UPDATE_CART_STORE } from 'yoda-site-components/lib/actionTypes/LocationServiceActionTypes';
import { addItemToCart } from 'yoda-interfaces/lib/Cart/CartApi';
import { loadEmptyCart } from '../actions/LoadEmptyCartAction';
import { hideGiftCardBannerMessage } from '../actions/GiftCardBannerAction';
import { initNewGiftCardBanner } from './GiftCardBannerSaga';
import { itemsQuantityChangedAction } from '../actions/ItemsQuantityChangedAction';
import {
    CART_DETAILS_GET_REQUEST,
    CART_DETAILS_GET_ERROR,
    CART_DETAILS_GET_REQUEST_SUCCESS,
    UPDATE_ANALYTICS_CLICK_EVENT,
    LOG_META_UA,
} from '../actionTypes/CartActionTypes';
import CartDetailsModel from '../helpers/CartDetailsModel';
import ApplicationLogger from '../helpers/ApplicationLogger';
import { deleteStoredCartItem } from '../actions/CartItemsAction';
import { getCartDetails } from '../actions/GetCartDetailsAction';
import CartService from '../services/CartService';

export const selectedStore = (state) => state.selectedStore;
export const orderItems = (state) => state.orderItems;
export const cartDetails = (state) => state.cartDetails;
export const coCoupons = (state) => state.coCoupons;
const eventTypes = ['REMOVE_ITEM', 'UPDATE_QUANTITY', 'REMOVE_SFL_ITEM', 'ADD_SFL_ITEM'];
const logger = ApplicationLogger.getLogger();
const locationServiceCookies = {
    akamaiStore: LOCATION_SERVICE_COOKIES_AKAMAI_STORE,
    userPreferredStore: LOCATION_SERVICE_COOKIES_USER_PREFERRED_STORE,
};

function* getBestCoupons(featureFlags, loadSingleClickCheckout, isFromMeta) {
    if (!loadSingleClickCheckout) {
        yield call(COCouponsSaga, { priceFlag: true, isFromMeta });
    }
}

function* logMetaDetails(logMessage, cookies = {}, response = {}) {
    if (__SERVER__) {
        const { cluster } = yield select(selectContext);
        doPostSSRLogs(
            logger,
            {
                requestInfo: `${logMessage} on SSR`,
                response,
            },
            cookies,
            cluster
        );
    } else {
        yield put({
            type: LOG_CLIENT_ERROR,
            payload: {
                errorType: `${logMessage} on CSR`,
                errorDescription: JSON.stringify(response),
            },
        });
    }
}

function* handleMetaAddToCart(isFromMeta, appContext, headers, logger) {
    if (!isFromMeta) return { isSuccess: true };

    const {
        query: { products } = {},
        preferences: { metaSocialSelling: { SkuIdLength = 11 } = {} },
    } = appContext;
    const cookies = (headers || {}).cookies || {};
    const { payload: ATCPayload, invalidEntries } = CartService.buildMetaATCPayload(
        products,
        SkuIdLength
    );
    if (invalidEntries.length > 0) {
        const userAgent = headers?.['user-agent'];
        const logData = { errorInfo: { invalidEntries, userAgent }, errorCode: 'sample' };
        yield call(logMetaDetails, 'Invalid meta cart payload', cookies, { data: logData });
    }
    if (!ATCPayload) {
        return { isSuccess: true };
    }

    const atcAction = { payload: ATCPayload, type: 'ADDTOCART_POST_REQUEST', headers };
    const addToCartResponse = yield call(FactorySaga, addItemToCart, atcAction, logger);

    if (!addToCartResponse.isSuccess && !addToCartResponse.guestAccountCreationFailed) {
        yield call(logMetaDetails, 'Meta add to cart failed', cookies, addToCartResponse);
    }

    return addToCartResponse;
}

/**
 * Triggers analytics for the skus that have been flipped to Same Day Pickup
 * @param {Object} oldCart Old cart respons
 * @param {Object} newCart New cart response
 */
function* triggerSDPUAnalytics(oldCart, newCart) {
    if (isEmpty(oldCart) || isEmpty(newCart)) {
        return;
    }
    const SDPUFlippedSkus = [];
    const itemsPath = 'data.bag.items';
    const oldBag = get(oldCart, itemsPath, []);
    const newBag = get(newCart, itemsPath, []);
    newBag.forEach((newBagEl) => {
        const oldBagEl = getCartItemById(oldBag, newBagEl.id);
        const isBopisInNewBag = isBOPISItem(newBagEl);
        const isNotBopisInOldBag = !isBOPISItem(oldBagEl);
        if (!isEmpty(oldBagEl) && isBopisInNewBag && isNotBopisInOldBag) {
            const sku = get(newBagEl, 'sku.id');
            sku && SDPUFlippedSkus.push(sku);
        }
    });

    if (SDPUFlippedSkus.length) {
        yield put({
            type: UPDATE_ANALYTICS_CLICK_EVENT,
            payload: {
                name: ANALYTICS_ACTIONS_SAME_DAY_PICKUP,
                skus: SDPUFlippedSkus,
            },
        });
    }
}

export function* cartDetailsSaga(actionParam) {
    const action = actionParam;
    const { headers = {} } = action;
    const geoDataHeader = headers && headers.get && headers.get('x-geodata');
    const cookies = (headers || {}).cookies || {};
    // DA => extract item quantity update or remove item event so that we can trigger them once get call is successful
    const quantityUpdateAction =
        action.payload === 'ITEM_QUANTITY_UPDATE' ? 'ITEM_QUANTITY_UPDATE' : null;
    const isInitFromApplePay = (action.payload || {}).initiatedFrom === 'applePay';
    const isInitFromApplyCouponSuccess = action.initiatedFrom === 'apply_coupon_success';
    const isInitGCBannerAddSuccess = action.initiatedFrom === 'add_gc_banner_success';
    const deliveryChangedItemsAurora = action.auroraHeaders;
    let appliedAdjustmentsResponse;
    if (action.payload && action.payload.appliedAdjustmentsResponse) {
        appliedAdjustmentsResponse = action.payload.appliedAdjustmentsResponse;
        delete action.payload.appliedAdjustmentsResponse;
    }
    const loadSingleClickCheckout = get(action, 'payload.loadSingleClickCheckout', false);
    const closeSingleClickCheckout = get(action, 'headers.payload.closeSingleClickCheckout', false);
    const isNative = yield select((state) => get(state, 'context.isNative', false));

    const context = (state) => state.context;
    const appContext = yield select(context);
    const {
        enableSinglePPIDRecommendation = true,
        enablePreSourcing = true,
        enableGiftCardBanner = true,
        enableSingleDeliveryDateSTH = true,
        enableAnalyticsCartIds = true,
        disableBnpl = false,
        enablelowStockNudgeAlert = true,
        enableCouponExpireNudgeAlert = true,
        disableNarvarShippingProtection = false,
    } = appContext?.featureFlags || {};
    const { couponExpireNudgeTime = 36 } = appContext?.preferences || {};
    // Never create new object of action. this is going to break down everything.
    // Query param to get the error messages
    action.payload = {
        expand: disableBnpl ? 'status,delivery' : 'status,delivery,billing',
        origin: 'cart',
    };
    const enableShippingProtection = __SERVER__
        ? cookies.enableShippingProtection
        : isFeatureEnabledFromCookie('enableShippingProtection');
    if (enableShippingProtection || !disableNarvarShippingProtection) {
        action.payload.enableShippingProtection = true;
    }

    if (loadSingleClickCheckout) {
        action.payload.origin = 'review';
        action.payload.expand = 'status,delivery,billing';
    }
    if (closeSingleClickCheckout || loadSingleClickCheckout) {
        action.payload.scc = 'true';
    }
    action.showRefresh = true;
    action.apiName = 'GET_CART_API';
    if (geoDataHeader) {
        action.payload.geoDataHeader = geoDataHeader;
    }
    action.bopisInfo = {};
    // Get the store id to pass as a param for BOPIS inventory
    let storeId = null;
    const userStoreLocationInfo = (state) => state.userStoreLocationInfo;
    const userStoreInfo = yield select(userStoreLocationInfo);
    action.bopisInfo.useOldCommonBopisService = false;
    if (enableGiftCardBanner) {
        yield put(hideGiftCardBannerMessage());
    }
    // pass storeId from common location service
    storeId = userStoreInfo && userStoreInfo.storeId;
    if (isNative && isEmpty(storeId) && __SERVER__) {
        const ups = cookies.userPreferredStore && JSON.parse(cookies.userPreferredStore);
        if (!isEmpty(ups)) {
            storeId = ups.storeId;
        }
    }
    if (!isEmpty(storeId)) {
        action.payload.storeId = storeId.replace(/"/g, '');
    }
    const deviceType = appContext && appContext.deviceType ? appContext.deviceType : '';
    // to pass all GET order service call to find device type.
    if (!isEmpty(deviceType)) {
        action.payload.clientId = getClientId(deviceType);
    }
    const ssrCookies = __SERVER__ && cookies;
    const uspZipCode = getZipCodeFromUPS(ssrCookies, locationServiceCookies);
    if (uspZipCode) {
        action.bopisInfo.zipCode = uspZipCode;
    }
    // pass single delivery date zipcode to header
    if (enableSingleDeliveryDateSTH) {
        const userZipcode = getUserZipCode(ssrCookies, true, locationServiceCookies);
        if (userZipcode) {
            action.payload.akZip = userZipcode;
        }
    }
    /*
     * Add client source if passed in action
     * */
    if (enablePreSourcing) {
        action.payload.xClientSource = action.xClientSource || 'CART';
    }
    yield put(singleClickCheckoutLoadApiFailed(''));
    const oldResponse = yield select(cartDetails);
    const { isFromMeta } = headers;
    const addToCartResponse = yield call(
        handleMetaAddToCart,
        isFromMeta,
        appContext,
        headers,
        logger
    );
    const shouldLoadCart = !isFromMeta || addToCartResponse.isSuccess;
    let result = {};
    if (shouldLoadCart) {
        [result] = yield all([
            call(FactorySaga, getOrderDetails, action, logger),
            call(getBestCoupons, appContext.featureFlags, loadSingleClickCheckout, isFromMeta),
        ]);
    }
    const { response } = result;
    let isCartLoadedInServerSide = result.isSuccess;
    let bagItemCount = 0;
    if (result.isSuccess) {
        /**
         * To add or update cookie 'DP_USER_DEFAULT_ADDRESS_ZIP' value based on shipping address zipcode.
         * To Add or Update UID Cookie
         */
        if (!__SERVER__) {
            const enableSingleDeliveryDateSTH = get(
                appContext.featureFlags,
                'enableSingleDeliveryDateSTH',
                true
            );
            enableSingleDeliveryDateSTH && updateUserZipcodeBasedSTHItem(result.response);
            updateCartUID(result.response);
        }

        // clear any removal message
        if (!action.retainRemovalMessage) {
            yield put(deleteStoredCartItem());
        }

        if (isInitFromApplyCouponSuccess) {
            yield put({
                type: COAdjustmentActionTypes.CO_ADJUSTMENTS_POST_SUCCESS_REQUEST,
                callbackValue: { ...action.callbackValue, orderDetails: result },
            });
        }
        if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.bag &&
            response.data.bag.itemCount === 0
        ) {
            const cartDetailModel = new CartDetailsModel(response);
            if (isNative) {
                if (action.requestType === 'REMOVE_ITEM') {
                    yield put(updateCartCount(bagItemCount, []));
                }
            }
            yield all([
                put(cartDetailModel.setOrderBannerDetails()),
                put(cartDetailModel.getOrderInfo()),
                put(loadEmptyCart(true)),
                put(cartDetailModel.getCertonaAttributes()),
                put(showGlobalLoader(false)),
                put(itemActions.updateItemCountAction(bagItemCount)),
                put({ type: CART_DETAILS_GET_REQUEST_SUCCESS, cartDetailsResponse: response }),
            ]);
            Cookies.remove('APPLIANCE_ZIP');
        } else {
            const cartDetailsResponse = result.response;
            cartDetailsResponse.gid = response.headers.get('x-default-gcid');
            const deliveryChangedItems =
                response.headers.get('X-ITEM-DELIVERY-CHANGED') || deliveryChangedItemsAurora;
            cartDetailsResponse.data.deliveryChangedItems = deliveryChangedItems;
            cartDetailsResponse.data.checkedStoreId = action.payload.storeId;

            if (!__SERVER__) {
                yield call(triggerSDPUAnalytics, oldResponse, cartDetailsResponse);
            }

            if (enableGiftCardBanner && !__SERVER__) {
                yield call(initNewGiftCardBanner, {
                    cartDetailsResponse,
                    isInitGCBannerAddSuccess,
                });
            }
            const cartDetailsModel = new CartDetailsModel(cartDetailsResponse);
            yield put(cartDetailsModel.setOrderBannerDetails());

            // MCYODA-4272 - reading item count from response header and setting it to cookie
            bagItemCount = response.headers.get('X-Item-Count');
            if (isNil(bagItemCount)) {
                // If the header is empty then check in the response for bag item count.
                bagItemCount =
                    (cartDetailsResponse.data.bag && cartDetailsResponse.data.bag.itemCount) || 0;
            }
            // MCYODA-4272 - adding itemCount to redux state
            if (bagItemCount) {
                response.bagItemCount = bagItemCount;
            }
            let cartItemsData = [];
            let cartItemsArr = [];
            if (isNative) {
                cartItemsData = cartDetailsResponse.data.bag.items.map((item) => {
                    const price = find(item?.totals, { name: 'ITEM AMOUNT' });
                    if (!item.discontinued && item.inventory.status === 'INSTOCK') {
                        return {
                            pprID: item?.product?.id,
                            quantity: item?.quantity,
                            price: price?.amount || '0.0',
                            skuid: item?.sku?.id,
                        };
                    }
                });
                cartItemsArr = cartItemsData.filter((item) => item);
                if (eventTypes.includes(action.requestType) && cartItemsArr.length > 0) {
                    yield put(updateCartCount(bagItemCount, cartItemsArr));
                }
            }
            cartDetailsResponse.applePayEligible = isOrderEligibleApplePay(
                appContext,
                cartDetailsResponse
            );
            cartDetailsResponse.googlePayEligibleOrder = isOrderEligibleGooglePay(
                appContext,
                cartDetailsResponse
            );
            cartDetailsResponse.isPayPalStrategiesAvailable = isPayPalStrategiesAvailable(
                cartDetailsResponse
            );
            yield put({ type: CART_DETAILS_GET_REQUEST_SUCCESS, cartDetailsResponse });
            if (appliedAdjustmentsResponse) {
                appliedAdjustmentsResponse.adjustments = get(
                    cartDetailsResponse,
                    'data.adjustments',
                    []
                );
                yield put({
                    type: COAdjustmentActionTypes.CO_ADJUSTMENTS_BY_ID_DELETE_SUCCESS,
                    appliedAdjustmentsResponse,
                });
            }

            if (!headers.preventCartSync) {
                const { type } = yield put(
                    cartDetailsModel.updateUserStoreLocationInfo(userStoreInfo)
                );

                if (!__SERVER__ && type === UPDATE_CART_STORE) {
                    yield delay(1e3);
                    yield put(getCartDetails({ preventCartSync: true }));
                    return;
                }
            }

            yield all([
                put(cartDetailsModel.getOrderInfo(appContext)),
                put(cartDetailsModel.getOrderTotals()),
                put(cartDetailsModel.getCartItems()),
                put(cartDetailsModel.getOrderAdjustments()),
                put(cartDetailsModel.getAppliedRewards()),
                put(cartDetailsModel.getOrderCharges()),
                put(
                    cartDetailsModel.getCertonaAttributes(
                        enableSinglePPIDRecommendation,
                        enableAnalyticsCartIds
                    )
                ),
                put(
                    cartDetailsModel.getCouponAlertInfo(
                        { enableCouponExpireNudgeAlert },
                        couponExpireNudgeTime
                    )
                ),
                put(cartDetailsModel.getNudgeAlertInfo({ enablelowStockNudgeAlert })),
                put(itemActions.updateItemCountAction(bagItemCount)),
                put(cartDetailsModel.getBXGXPromotionMessages()),
                put(loadEmptyCart(false)),
                put(showGlobalLoader(false)),
            ]);

            const featureFlags = (appContext || {}).featureFlags || {};
            const { enableDiscontinuedItemsMessaging = true } = featureFlags;
            if (isNative && __SERVER__) {
                yield call(getBestCoupons, featureFlags, loadSingleClickCheckout);
            }
            enableDiscontinuedItemsMessaging
                ? yield put(cartDetailsModel.discontinuedOOSErrors())
                : yield put(cartDetailsModel.checkErrors());
            /** MCYODA-31152 - interim soultion for B2bAgent to restrict checkout of marketplaceitem */
            if (isAgentLogged(appContext)) yield put(cartDetailsModel.checkB2bAgentMktErrors());
            // extract available quantity changed items
            const availableQuantityChangedItems = result.response.headers.get(
                'X-ITEM-QUANTITY-CHANGED'
            );
            const quantityChangedItems = availableQuantityChangedItems
                ? availableQuantityChangedItems.split(',')
                : [];
            yield put(itemsQuantityChangedAction(quantityChangedItems));

            // DA => Once Item quantity is updated and store is updated with latest data,
            // trigger 'cartUpdate' analytics event
            if (quantityUpdateAction === 'ITEM_QUANTITY_UPDATE') {
                yield put({
                    type: UPDATE_ANALYTICS_CLICK_EVENT,
                    payload: ANALYTICS_ACTIONS_UPDATE_CART,
                });
            }
            /* Below action would be triggered to let native app to know add to cart or
             * updated cart item count */
            if (isNative) {
                const oldBagItemCount = get(oldResponse, 'data.bag.itemCount', 0);
                const latestBagItemCount = get(cartDetailsResponse, 'data.bag.itemCount', 0);
                if (
                    quantityUpdateAction !== 'ITEM_QUANTITY_UPDATE' &&
                    latestBagItemCount > oldBagItemCount
                ) {
                    yield put(addToCart(cartItemsArr));
                }
                if (!isEqual(oldBagItemCount, latestBagItemCount)) {
                    yield put(updateCartCount(bagItemCount, cartItemsArr));
                }
            }
            const cartItems = yield select(orderItems);
            if (isEmpty(storeId) && !isEmpty(cartItems)) {
                let bopisOption = null;
                forEach(cartItems, (item) => {
                    bopisOption = find(
                        item.deliveryOptions,
                        (option) => option.code === 'BOPIS' && option.selected
                    );
                });
                if (bopisOption) {
                    yield put(orderActions.getStoreByStoreId({ storeId: bopisOption.storeId }));
                }
            }
            // In Aurora add Cookies or LocalStorage for SDPUStoreInfo
            if (isAurora()) {
                let bopisOption = null;
                cartItems.forEach((item) => {
                    const itemData = item.deliveryOptions.find(
                        (option) => option.code === 'BOPIS' && option.selected
                    );
                    itemData && (bopisOption = itemData);
                });
                if (bopisOption) {
                    const sdpuObj = {
                        storeId: bopisOption.storeId,
                        storeName: bopisOption.storeName,
                        curbsidePickupIndicator: bopisOption.curbsidePickUpAvailable,
                    };
                    LocalStorage.setData('SDPUStoreInfo', sdpuObj, true);
                } else {
                    LocalStorage.removeData('SDPUStoreInfo', true);
                }
            }
            if (action.errorHandlerInfo && action.errorHandlerInfo.isApplePay) {
                yield put({
                    type: SET_SERVICE_ERROR,
                    errorHandlerInfo: action.errorHandlerInfo,
                });
            }
            if (closeSingleClickCheckout) {
                yield put(pageLoadSuccessAnalyticsAction('cart'));
            }
            const { enableCurbsideBanner = true } = featureFlags;
            if (!__SERVER__ && enableCurbsideBanner) {
                const showCurbsideBanner = getCurbsideEligible(
                    response.data.bag.items || [],
                    'cart'
                );
                if (showCurbsideBanner) {
                    const messageContentData = (state) => state.messageContentData;
                    const messageContent = yield select(messageContentData);
                    if (messageContent && !messageContent[CURBSIDE_CART_CONTENT]) {
                        yield put(getMessageContent(CURBSIDE_CART_CONTENT));
                    }
                }
            }
        }
        if (actionParam.isRewardsSlider) {
            appliedAdjustmentsResponse = {
                adjustments: response.data.adjustments,
                lastTouchedAdjustment: {
                    ...response.data.adjustments[0],
                    method: 'selected from list',
                },
            };
            yield put({
                type: COAdjustmentActionTypes.CO_ADJUSTMENTS_POST_SUCCESS,
                appliedAdjustmentsResponse,
            });
            yield put({
                type: ANALYTICS_CLICK_EVENT,
                payload: 'COUPON_APPLIED_SUCCESS',
            });
        }
    } else if (
        response &&
        response.status === 400 &&
        response.data &&
        response.data.length > 0 &&
        response.data[0].errorCode === 'SRV_ORD_ERR_ITM_NF'
    ) {
        const cartDetailsModel = new CartDetailsModel(response);
        LocalStorage.setData('ItemCount', 0, true);
        yield all([
            put(deleteStoredCartItem()),
            put(loadEmptyCart(true)),
            put(cartDetailsModel.getCertonaAttributes(enableSinglePPIDRecommendation)),
            put(showGlobalLoader(false)),
            put(itemActions.updateItemCountAction(bagItemCount)),
        ]);
        if (loadSingleClickCheckout) {
            yield put(singleClickCheckoutLoadApiFailed('empty-scroll'));
        }
        Cookies.remove('APPLIANCE_ZIP');
        isCartLoadedInServerSide = true;
    } else {
        yield all([
            put(deleteStoredCartItem()),
            put({ type: CART_DETAILS_GET_ERROR, response }),
            put(showGlobalLoader(false)),
        ]);
        /** handle ServiceError Message Box in ATC Slider
         * for other GET FO page will display Refresh Modal
         */
        const ATCServiceError = get(action, 'ATCPayload.pageFrom', '');
        const errorHandler = get(action, 'payload.errorHandler', {});
        errorHandler.errorComponent = 'ATC_SECTION';
        ATCServiceError
            ? yield put({
                  type: SET_SERVICE_ERROR,
                  errorHandlerInfo: action.payload.errorHandler,
              })
            : '';
        if (loadSingleClickCheckout) {
            yield put(singleClickCheckoutLoadApiFailed('error'));
        }
    }
    if (!isInitFromApplePay) {
        yield put(setApplePayOrderError({}));
    }
    const { cluster } = yield select(selectContext);
    doPostSSRLogs(
        logger,
        {
            requestInfo: `IS CART PAGE RENDERED ON SERVER SIDE : ${isCartLoadedInServerSide}`,
            response,
        },
        cookies,
        cluster
    );
}

function* logMetaUASaga(action) {
    const { headers = {} } = action;
    const cookies = (headers || {}).cookies || {};
    const userAgent = headers?.['user-agent'];
    yield call(logMetaDetails, `--${userAgent} Meta User Agent not found`, cookies);
}

const watchCartDetails = function* watchCartDetails() {
    const task = yield takeLatest(CART_DETAILS_GET_REQUEST, cartDetailsSaga);
    const context = (state) => state.context;
    const appContext = yield select(context);
    const { maxCartResponseTime = 4000 } = (appContext || {}).featureFlags || {};
    if (__SERVER__ && !isEmpty(maxCartResponseTime) && maxCartResponseTime > 0) {
        yield call(delay, maxCartResponseTime);
        task.cancel();
    }
    yield takeLatest(LOG_META_UA, logMetaUASaga);
};
watchCartDetails.sagaName = 'watchCartDetails';

export default watchCartDetails;
